import axios from 'axios'
import Swal from 'sweetalert2/dist/sweetalert2.js'
import store from '~/store'
import router from '~/router'
import i18n from '~/plugins/i18n'
import Vue from 'vue'

window.cancelSource = axios.CancelToken.source()

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    if (axios.isCancel(event.reason)) {
      event.preventDefault()
    }
  })
}

const requestInterceptor = config => {
  // Don't clobber a token the caller supplied - hierarchy hands its own in so a
  // new tree request can cancel the one still in flight, and overwriting it here
  // meant that cancel never reached the request and a slow earlier response
  // could land on top of a newer one. Those requests opt out of the
  // cancel-on-route-change below, which is the caller's call to make.
  if (!config.cancelToken) {
    config.cancelToken = window.cancelSource.token
  }

  return config
}

// Request interceptor
axios.interceptors.request.use(requestInterceptor)
axios.interceptors.request.use(request => {
  const token = store.getters['auth/token']
  if (token) {
    request.headers.common.Authorization = `Bearer ${token}`
  }

  const locale = store.getters['lang/locale']
  if (locale) {
    request.headers.common['Accept-Language'] = locale
  }

  // request.headers['X-Socket-Id'] = Echo.socketId()

  return request
})

// axios 0.21 aborts a cancelled request before it rejects it:
//
//   request.abort()   // fires onabort, which rejects with 'Request aborted'
//   reject(cancel)    // too late, the promise already settled
//
// abort() only fires onabort while the request is actually in flight, so a
// cancel lands as a plain ECONNABORTED error rather than a Cancel exactly when
// the user navigates away mid-request. isCancel() misses those and they fall
// through to the generic network-error toast below. Treat them as cancels.
// Timeouts share the ECONNABORTED code but carry their own message and should
// still surface, so match on the message too.
const isAbortedRequest = error =>
  error.code === 'ECONNABORTED' && error.message === 'Request aborted'

// The public mega-menu payload is fetched best-effort: its store degrades to an
// empty nav on failure (mega-menu/fetchPublicMenu) and re-fetches after every
// admin save. Letting the global network/5xx toast fire for it means a flaky
// header refetch flashes "something went wrong" on an unrelated page — and taints
// an otherwise successful save, which awaits that same refetch. The store owns
// this request's failure, so the interceptor stays silent for it.
// The header global-search typeahead degrades to empty results on failure (the search
// store swallows the error), so a flaky keystroke request must not flash a network/5xx
// toast over whatever page the user is on. The store owns this request's failure.
// The recipient typeahead behind the email modals degrades to an empty option list on
// failure and the user can still type an address by hand, so a flaky keystroke request
// must not flash a network/5xx toast over the modal. The component owns this failure.
const ownsErrorToast = error => {
  const url = error.config?.url || ''
  return url === '/api/mega-menu' ||
    url.startsWith('/api/search') ||
    url.startsWith('/api/buyer/recipient-search')
}

// Response interceptor
axios.interceptors.response.use(response => response, error => {
  if (axios.isCancel(error) || isAbortedRequest(error)) {
    return Promise.reject(error)
  }

  if (!error.response) {
    if (!ownsErrorToast(error)) {
      Vue.$toast.error(i18n.t('error_alert_text'), {
        timeout: 2000,
        hideProgressBar: true,
        transition: 'Vue-Toastification__fade',
        containerClassName: 'p-v-toast'
      })
    }
    return Promise.reject(error)
  }

  const { status } = error.response

  if (status === 401 && store.getters['auth/check']) {
    store.commit('auth/LOGOUT')
    router.push({ name: 'login' })
  }

  if (status === 404) {
    const requestUrl = error.config?.url || ''
    // Blob downloads (e.g. JPG image zips) carry their 404 message in the Blob
    // body, which this interceptor can't read; the calling action parses it and
    // shows the proper toast, so skip the generic one here to avoid a duplicate.
    // Removing a product reports a 404 ("already removed") inside its own confirmation
    // dialog, which the user is looking at; a toast on top would say it twice.
    const skipNotFoundToast = requestUrl.includes('/api/add-product-by-design-number') ||
      requestUrl.includes('/jpg-images') ||
      /\/api\/admin\/products\/\d+$/.test(requestUrl)
    if (!skipNotFoundToast) {
      const errorMessage = error.response.data?.message || i18n.t('not_found_alert_text')

      Vue.$toast.error(errorMessage, {
        timeout: 2000,
        hideProgressBar: true,
        transition: 'Vue-Toastification__fade',
        containerClassName: 'p-v-toast'
      })
    }
  }
  if (status === 429) {
    // The scan action renders its own inline message next to the Design # input, so let
    // the rejection through instead of swallowing it here — swallowing returns a promise
    // that never settles, and the awaiting scan would hang rather than report anything.
    // Both of these await the rejection: swallowing it returns a promise that never settles,
    // which strands the caller's finally — the scan would hang, and the line sheet builder
    // would spin "Generating preview…" forever instead of reporting the rate limit.
    const isRetryableRateLimit =
      (error.config?.url || '').includes('/api/add-product-by-design-number') ||
      (error.config?.url || '').includes('/line-sheet/preview')

    if (!isRetryableRateLimit) {
      Vue.$toast.error(i18n.t('error_rate_limited'), {
        timeout: 3000,
        hideProgressBar: true,
        transition: 'Vue-Toastification__fade',
        containerClassName: 'p-v-toast'
      })
      // Don't pass 429 error to components - handled globally
      return new Promise(() => {})
    }
  }

  if (status >= 500 && !ownsErrorToast(error)) {
    serverError(error.response)
  }

  return Promise.reject(error)
})

let serverErrorModalShown = false
async function serverError(response) {
  if (serverErrorModalShown) return

  const contentType = response.headers['content-type'] || ''

  if (contentType.includes('text/html')) {
    const iframe = document.createElement('iframe')
    const responseData = response.data instanceof Blob ? await response.data.text() : response.data
    iframe.srcdoc = responseData

    Swal.fire({
      html: iframe.outerHTML,
      showConfirmButton: false,
      customClass: { container: 'server-error-modal' },
      didDestroy: () => { serverErrorModalShown = false },
      grow: 'fullscreen',
      padding: 0
    })

    serverErrorModalShown = true
  } else {
    const isServerError = response.status === 500

    Vue.$toast.error(isServerError
      ? response.data?.message ?? i18n.t('error_alert_text')
      : i18n.t('error_alert_text'), {
      timeout: 2000,
      hideProgressBar: true,
      transition: 'Vue-Toastification__fade',
      containerClassName: 'p-v-toast'
    })
  }
}
